import { createSelector } from "reselect"
import { get, chain, filter, every, size, some, isEmpty, startsWith, find, isArray } from "lodash"
import links from "../navigation.json"
import { getIsSuperuser } from "."
import { getSelectedModelId } from "./portfolios"

const EMPTY_ARRAY = []

const getAccount = state => get(state, "global.selected.account")
const getAccountMeta = state => get(state, "global.selected.accountMeta")
const getPortfolio = state => get(state, "global.selected.portfolio")
const getPortfolioIntradayEnabled = state => get(state, "global.data.portfolio.data.intradayEnabled", false)
const getModelId = state => getSelectedModelId(state)
const getPathname = () => window.location.pathname
const getPortfolioExperiments = state => get(state, "global.data.portfolioExperiments.data.portfolio.experiments")

// We need to remove the Rebalance tab completely for OpPro accounts without experiments
function shouldKeepLink(plan, modelId, nbExperiments, portfolioIntradayEnabled) {
  return link => {
    // No omit conditions
    if (!link.omit) { return true }

    const { plan: linkOmitPlan, modelId: linkModelId, noExperiments, needIntraday } = link.omit
    // Restrictive rules are simpler
    const restrict = some([
      linkModelId && linkModelId === modelId,
      needIntraday && !portfolioIntradayEnabled,
      (linkOmitPlan && !noExperiments) && linkOmitPlan === plan,
      (linkOmitPlan && noExperiments) && linkOmitPlan === plan && nbExperiments === 0,
    ])

    return !restrict
  }
}

const allConditionsFilter = (item, modelId, plan, betaFeaturesEnabled, isSuperuser, nbExperiments, portfolioIntradayEnabled) => {
  const restrictCondition = item => !item.restrict || item.restrict === modelId
  const omitCondition = shouldKeepLink(plan, modelId, nbExperiments, portfolioIntradayEnabled)
  const betaCondition = item => !item.betaFeaturesEnabled || betaFeaturesEnabled
  const superuserCondition = item => !item.superuserOnly || isSuperuser

  return every([restrictCondition, omitCondition, betaCondition, superuserCondition], (fn) => fn(item))
}

function getDisabled(subItem, accountMeta) {
  const { disabled } = subItem
  const { plan } = accountMeta
  if (isEmpty(disabled)) { return false }

  const omitConditionPlan = get(disabled, "plan")
  if (omitConditionPlan === plan) {
    return "Only available with enterprise plan"
  }
  return false
}

/**
 * Determines the relative path for a link. If `link.to` starts with a `/`, it is assumed that the path
 * starts at the account root.
 */
function getLinkPath(account, portfolio, link) {
  return startsWith(link.to, "/") ? `/${account}${link.to}` : `/${account}/p/${portfolio}/${link.to}`
}

function computeSubItems(subItems, account, accountMeta, portfolio, to, modelId, isSuperuser, portfolioIntradayEnabled) {
  const { plan, betaFeaturesEnabled } = accountMeta ?? {}

  return chain(subItems)
    .map(subItem =>
      ({
        ...subItem,
        id: subItem.to,
        to: `/${account}/p/${portfolio}/${to}/${subItem.to}`,
        hidden: subItem.hidden,
        disabled: getDisabled(subItem, accountMeta),
      }))
    .filter(subItem => allConditionsFilter(subItem, modelId, plan, betaFeaturesEnabled, isSuperuser, 0, portfolioIntradayEnabled))
    .value()
}

const getHiddenFullLinks = createSelector(
  [getAccount, getAccountMeta, getPortfolio, getModelId, getIsSuperuser, getPortfolioIntradayEnabled],
  (account, accountMeta, portfolio, modelId, isSuperuser, portfolioIntradayEnabled) => links.map(link =>
    ({
      ...link,
      id: link.to,
      to: getLinkPath(account, portfolio, link),
      subItems: computeSubItems(link.subItems, account, accountMeta, portfolio, link.to, modelId, isSuperuser, portfolioIntradayEnabled),
    }))
    .filter(link => link.hidden),
)

const getLinksIncludeHidden = createSelector(
  [getAccount, getAccountMeta, getPortfolio, getModelId, getPortfolioExperiments, getIsSuperuser, getPortfolioIntradayEnabled],
  (account, accountMeta, portfolio, modelId, portfolioExperiments, isSuperuser, portfolioIntradayEnabled) => {
    const { plan, betaFeaturesEnabled } = accountMeta ?? {}
    return chain(links)
      .map(link =>
        ({
          ...link,
          id: link.to,
          to: getLinkPath(account, portfolio, link),
          subItems: computeSubItems(link.subItems, account, accountMeta, portfolio, link.to, modelId, isSuperuser, portfolioIntradayEnabled),
          hidden: link.hidden,
        }))
      .filter(link => allConditionsFilter(link, modelId, plan, betaFeaturesEnabled, isSuperuser, size(portfolioExperiments), portfolioIntradayEnabled))
      .value()
  },
)

function getSubLinkItems(links, pathname) {
  const parentLink = find(links, link => pathname.startsWith(link.to))

  if (!parentLink || isEmpty(parentLink.subItems) || !isArray(parentLink.subItems)) {
    return EMPTY_ARRAY
  }

  return parentLink.subItems.map(subItem => ({
    ...subItem,
    parentId: parentLink.id,
  }))
}

const getSubLinksIncludeHidden = createSelector(
  [getLinksIncludeHidden, getPathname],
  getSubLinkItems,
)

export const getFullLinks = createSelector(
  [getLinksIncludeHidden],
  (links) => filter(links, link => !link.hidden),
)

export const getSubLinks = createSelector(
  [getFullLinks, getPathname],
  getSubLinkItems,
)

export const getControls = createSelector(
  [getLinksIncludeHidden, getSubLinksIncludeHidden, getModelId, getPathname],
  (links, subLinks, modelId, pathname) => {
    if (subLinks && subLinks.length > 0) {
      return chain(subLinks)
        .filter(link => link.hidden || pathname.startsWith(link.to))
        .first()
        .get("controls", {})
        .value()
    } else {
      return chain(links)
        .filter(link => pathname.startsWith(link.to))
        .first()
        .get("controls", {})
        .value()
    }
  },
)

export const getIsNavigationDisabled = createSelector(
  [getHiddenFullLinks, getPathname],
  (links, pathname) => {
    if (!links || links.length === 0) {
      return false
    }

    return chain(links)
      .filter(link => pathname.startsWith(link.to))
      .first()
      .get("hidden", false)
      .value()
  },
)
